/**
 * Vue Composable for Form Validation
 * Provides reactive validation state and methods for easy integration
 */

import { ref, reactive, computed } from 'vue';
import validator, { ValidationSchemas } from '../utils/validation.js';
import toastStore from '../store/toast.store.js';

export function useValidation(schema, options = {}) {
  
  // Validation state
  const isValid = ref(true);
  const errors = ref([]);
  const fieldErrors = reactive({});
  const isSubmitting = ref(false);
  
  // Options
  const {
    showToast = true,
    toastDuration = 3000,
    validateOnChange = false
  } = options;

  /**
   * Validates data against the schema
   * @param {Object} data - Data to validate
   * @param {Object} customSchema - Optional custom schema override
   * @returns {Object} - Validation result
   */
  const validate = (data, customSchema = null) => {
    const validationSchema = customSchema || schema;
    const result = validator.validate(data, validationSchema);
    
    isValid.value = result.isValid;
    errors.value = result.errors;
    
    // Update field-specific errors
    updateFieldErrors(result.errors);
    
    return result;
  };

  /**
   * Updates field-specific error state
   * @param {Array} validationErrors - Array of validation errors
   */
  const updateFieldErrors = (validationErrors) => {
    // Clear previous field errors
    Object.keys(fieldErrors).forEach(key => {
      delete fieldErrors[key];
    });
    
    // Set new field errors
    validationErrors.forEach(error => {
      fieldErrors[error.field] = error.message;
    });
  };

  /**
   * Validates a single field
   * @param {string} fieldName - Name of the field to validate
   * @param {*} value - Value to validate
   * @returns {boolean} - Whether the field is valid
   */
  const validateField = (fieldName, value) => {
    if (!schema.properties || !schema.properties[fieldName]) {
      return true;
    }

    const fieldSchema = {
      type: 'object',
      properties: {
        [fieldName]: schema.properties[fieldName]
      }
    };

    const fieldData = { [fieldName]: value };
    const result = validator.validate(fieldData, fieldSchema);
    
    if (result.isValid) {
      delete fieldErrors[fieldName];
    } else {
      fieldErrors[fieldName] = result.errors[0]?.message || 'Invalid value';
    }
    
    return result.isValid;
  };

  /**
   * Clears all validation errors
   */
  const clearErrors = () => {
    isValid.value = true;
    errors.value = [];
    Object.keys(fieldErrors).forEach(key => {
      delete fieldErrors[key];
    });
  };

  /**
   * Clears error for a specific field
   * @param {string} fieldName - Name of the field to clear
   */
  const clearFieldError = (fieldName) => {
    delete fieldErrors[fieldName];
  };

  /**
   * Gets error message for a specific field
   * @param {string} fieldName - Name of the field
   * @returns {string|null} - Error message or null
   */
  const getFieldError = (fieldName) => {
    return fieldErrors[fieldName] || null;
  };

  /**
   * Checks if a field has an error
   * @param {string} fieldName - Name of the field
   * @returns {boolean} - Whether the field has an error
   */
  const hasFieldError = (fieldName) => {
    return fieldName in fieldErrors;
  };

  /**
   * Shows validation errors as toast messages
   * @param {Array} validationErrors - Array of validation errors
   */
  const showValidationToasts = (validationErrors) => {
    if (!showToast || !validationErrors.length) return;

    // Show first error as main toast
    const firstError = validationErrors[0];
    toastStore.dispatch('addMessage', {
      message: firstError.message,
      icon: 'error',
      ttl: toastDuration
    });

    // Show additional errors if there are more than one
    if (validationErrors.length > 1) {
      setTimeout(() => {
        toastStore.dispatch('addMessage', {
          message: `And ${validationErrors.length - 1} more validation error${validationErrors.length > 2 ? 's' : ''}`,
          icon: 'error',
          ttl: toastDuration
        });
      }, 500);
    }
  };

  /**
   * Validates and shows errors with toast notifications
   * @param {Object} data - Data to validate
   * @param {Object} customSchema - Optional custom schema
   * @returns {boolean} - Whether validation passed
   */
  const validateWithToast = (data, customSchema = null) => {
    const result = validate(data, customSchema);
    
    if (!result.isValid) {
      showValidationToasts(result.errors);
    }
    
    return result.isValid;
  };

  /**
   * Handles form submission with validation
   * @param {Object} data - Form data to validate
   * @param {Function} submitFn - Function to call if validation passes
   * @param {Object} customSchema - Optional custom schema
   * @returns {Promise} - Promise that resolves with submission result
   */
  const handleSubmit = async (data, submitFn, customSchema = null) => {
    if (isSubmitting.value) return;
    
    isSubmitting.value = true;
    
    try {
      // Validate data
      if (!validateWithToast(data, customSchema)) {
        return { success: false, errors: errors.value };
      }

      // Call submit function
      const result = await submitFn(data);
      
      // Clear errors on success
      clearErrors();
      
      return { success: true, result };
    } catch (error) {
      console.error('Submission error:', error);
      
      // Show error toast
      if (showToast) {
        toastStore.dispatch('addMessage', {
          message: error.message || 'An error occurred while submitting',
          icon: 'error',
          ttl: toastDuration
        });
      }
      
      return { success: false, error };
    } finally {
      isSubmitting.value = false;
    }
  };

  /**
   * Creates a reactive form with validation
   * @param {Object} initialData - Initial form data
   * @returns {Object} - Reactive form object with validation
   */
  const createForm = (initialData = {}) => {
    const form = reactive({ ...initialData });
    
    // Watch for changes if validateOnChange is enabled
    if (validateOnChange) {
      // This would require a watcher, but for now we'll provide manual validation
    }
    
    return {
      form,
      validate: () => validate(form),
      validateField: (fieldName) => validateField(fieldName, form[fieldName], form),
      clearErrors,
      clearFieldError,
      getFieldError,
      hasFieldError,
      isValid: computed(() => isValid.value),
      errors: computed(() => errors.value),
      fieldErrors: computed(() => fieldErrors)
    };
  };

  // Computed properties
  const hasErrors = computed(() => errors.value.length > 0);
  const errorCount = computed(() => errors.value.length);

  /**
   * Checks if the form is valid for submission
   * @param {Object} data - Form data to validate
   * @param {Object} customSchema - Optional custom schema override
   * @returns {boolean} - Whether the form is valid
   */
  const isFormValid = (data, customSchema = null) => {
    const validationSchema = customSchema || schema;
    const result = validator.validate(data, validationSchema);
    return result.isValid;
  };

  return {
    // State
    isValid,
    errors,
    fieldErrors,
    isSubmitting,
    hasErrors,
    errorCount,
    
    // Methods
    validate,
    validateField,
    validateWithToast,
    handleSubmit,
    createForm,
    clearErrors,
    clearFieldError,
    getFieldError,
    hasFieldError,
    showValidationToasts,
    isFormValid
  };
}

/**
 * Composable for specific model validation
 * @param {string} modelName - Name of the model (Song, Playlist, Artist, etc.)
 * @param {Object} options - Validation options
 * @returns {Object} - Validation composable
 */
export function useModelValidation(modelName, options = {}) {
  const schema = ValidationSchemas[modelName];
  
  if (!schema) {
    console.warn(`Validation schema for model '${modelName}' not found`);
    return useValidation({}, options);
  }
  
  return useValidation(schema, options);
}

/**
 * Quick validation function for simple use cases
 * @param {Object} data - Data to validate
 * @param {string|Object} schema - Schema name or schema object
 * @param {Object} options - Validation options
 * @returns {Object} - Validation result
 */
export function quickValidate(data, schema, options = {}) {
  const schemaObj = typeof schema === 'string' ? ValidationSchemas[schema] : schema;
  
  if (!schemaObj) {
    console.warn(`Schema '${schema}' not found`);
    return { isValid: false, errors: [{ field: 'root', message: 'Schema not found' }] };
  }
  
  const result = validator.validate(data, schemaObj);
  
  // Show toast if validation fails and showToast is enabled
  if (!result.isValid && options.showToast !== false) {
    const firstError = result.errors[0];
    toastStore.dispatch('addMessage', {
      message: firstError.message,
      icon: 'error',
      ttl: options.toastDuration || 3000
    });
  }
  
  return result;
}

export default useValidation;
